import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import CaptionsBubbleIcon from '@src/svgs/CaptionsBubbleIcon';
import RedlinesIcon from '@src/svgs/RedlinesIcon';
import TranscriptIcon from '@src/svgs/TranscriptIcon';
import React from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { REDLINE_REMOVED_COLOR } from './redlineColors';

import type { RedlineView } from './hooks/useRedlineToggle';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

export type TranscriptMode = 'transcript' | 'captions' | 'redlines';

const TOOLTIP_ID = 'transcript-view-tooltip';

export interface TranscriptModeToggleProps {
  /** Whether the transcript is showing caption lines right now. */
  captionsView: boolean;
  /** Owns the view flag (`showSubtitles`) the toggle flips. */
  videoCreator: Pick<
    ITranscriptVideoCreator,
    'setShowSubtitles' | 'openCaptionsView' | 'leaveCaptionsView'
  >;
  /** No caption track yet: the Captions segment says it will make one. */
  captionsAvailable: boolean;
  /**
   * The host always shows caption lines (Content Studio's Captions tab), so
   * there is no view to pick: render nothing.
   */
  forceCaptionsView?: boolean;
  /**
   * PLAT-11877 — the redlines view, the third segment (it used to be a
   * toggle of its own beside Cap). Omitted where it isn't offered (reviewer
   * mode).
   */
  redlines?: RedlineView;
}

/**
 * PLAT-11689 / PLAT-11877 — which view the Transcript panel shows: the clip's
 * text, one row per caption line, or the redlines of the transcript's edits.
 *
 * A persistent three-way segmented control, not a dropdown: every view is one
 * click away and the current one is always visible — the dropdown buried the
 * switch behind a second click (feedback on #3645). Icons, because the toolbar
 * is 380px wide beside Search, Undo/Redo and the globe; each segment names
 * itself and says what it does on hover. Built on the Voice control's
 * segmented styling (`AudioModeSegmentedToggle`), gold active state: a view
 * switch is a display control (design-system.md).
 */
export const TranscriptModeToggle: React.FC<TranscriptModeToggleProps> = ({
  captionsView,
  videoCreator,
  captionsAvailable,
  forceCaptionsView,
  redlines,
}) => {
  if (forceCaptionsView) return null;
  const redlineSubject = redlines?.isRedlineSurface
    ? 'transcript redlines'
    : 'replaced terms';
  const options: {
    id: TranscriptMode;
    label: string;
    hint: string;
    icon: React.ReactNode;
    inertHint?: string;
  }[] = [
    {
      id: 'transcript',
      // "Clip Text", not "Text" (PLAT-11877): the tab above is already
      // called Transcript, and the words belong to the clip on every surface.
      label: 'Clip Text',
      hint: 'This clip’s spoken words',
      icon: (
        <TranscriptIcon width="14" height="14" strokeColor="currentColor" />
      ),
    },
  ];
  if (redlines) {
    options.push({
      id: 'redlines',
      label: redlines.isRedlineSurface ? 'Redlines' : 'Replaced Terms',
      hint: redlines.isRedlineSurface
        ? 'Full transcript, edits marked'
        : 'Terms replaced in the transcript',
      // The struck page: the Clip Text page with one line struck in the
      // redlines' red — the view shows edits, it does not make them. Pressed
      // (gold) the strike goes monochrome so the segment reads as one shape.
      icon: (
        <RedlinesIcon
          width="14"
          height="14"
          strokeColor="currentColor"
          strikeColor={
            redlines.isOn || redlines.blocked
              ? 'currentColor'
              : REDLINE_REMOVED_COLOR
          }
        />
      ),
      inertHint: redlines.blocked
        ? `Turn off "Show Only" to see ${redlineSubject}`
        : undefined,
    });
  }
  // Captions sits last (PLAT-12043, review on PLAT-11877): redlines is
  // opened more often than captions, so the rarer view takes the far right.
  options.push({
    id: 'captions',
    label: 'Captions',
    // PLAT-11877 — with no track yet the pick also turns captions on, in
    // the showcase's default style; the segment says so instead of going
    // inert.
    hint: captionsAvailable
      ? 'One row per caption line'
      : 'Turns captions on, one row per line',
    icon: (
      <CaptionsBubbleIcon width="20" height="16" strokeColor="currentColor" />
    ),
  });
  const mode: TranscriptMode = redlines?.isOn
    ? 'redlines'
    : captionsView
      ? 'captions'
      : 'transcript';
  // Redlines and caption lines turn each other off (Transcript's effects), so
  // each pick only turns its own view on; Transcript turns off whichever is.
  // Hosts own the "Captions" pick (show hidden captions; Creator Studio also
  // opens the captions editor); a host without one just flips the view.
  const onModeChange = (next: TranscriptMode) => {
    if (next === mode) return;
    if (next === 'redlines') {
      redlines?.onToggle();
    } else if (next === 'captions') {
      // PLAT-11877 — leave Redlines here, in the same tick, rather than from
      // the host's hand-off effect a render later: one scroll lock, one
      // navigation, one fewer render pass on the way out.
      if (redlines?.isOn) redlines.onToggle();
      if (videoCreator.openCaptionsView) videoCreator.openCaptionsView();
      else videoCreator.setShowSubtitles(true);
    } else {
      if (redlines?.isOn) redlines.onToggle();
      if (captionsView) videoCreator.setShowSubtitles(false);
    }
    // PLAT-11877 — leaving the Captions view for either other view puts a
    // host's captions editor back where it was before the pick.
    if (captionsView && next !== 'captions') videoCreator.leaveCaptionsView?.();
  };

  return (
    <>
      <Segments
        role="group"
        aria-label="Transcript view"
        data-testid="transcript-view-mode"
      >
        {options.map(option => {
          const inert = !!option.inertHint;
          const active = mode === option.id;
          return (
            <Segment
              key={option.id}
              type="button"
              tabIndex={-1}
              className={active ? 'active' : ''}
              aria-label={option.label}
              aria-pressed={active}
              // Design system: inert controls use `aria-disabled`, never the
              // native attribute, so the segment can still say why.
              aria-disabled={inert || undefined}
              data-testid={`transcript-view-mode-${option.id}`}
              data-tooltip-id={TOOLTIP_ID}
              data-tooltip-content={
                option.inertHint ?? `${option.label} · ${option.hint}`
              }
              onClick={() => {
                if (inert) return;
                onModeChange(option.id);
              }}
            >
              {option.icon}
            </Segment>
          );
        })}
      </Segments>
      {createPortal(
        <Tooltip
          id={TOOLTIP_ID}
          style={COMPACT_TOOLTIP_STYLE}
          place="bottom"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </>
  );
};

// The Voice control's strip, at the toolbar's 26px height.
const Segments = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  height: 26px;
  box-sizing: border-box;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: 6px;
`;

const Segment = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  min-width: 28px;
  padding: 0 6px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover:not(.active) {
    color: var(--foreground);
  }
  &.active {
    background: var(--accent);
    color: var(--accent-foreground, #fff);
  }
  &[aria-disabled='true'] {
    cursor: default;
    color: var(--text-muted);
  }
  &[aria-disabled='true']:hover {
    color: var(--text-muted);
  }
`;
